import { Contents, Rendition, EpubCFI } from 'epubjs';
import type { Location as EpubLocation } from 'epubjs';
import View from 'epubjs/types/managers/view';
import type { AdditionalStylesheets } from '../../types';
import useRenditionStore from '../../stores/rendition';
import useSettingStore from '../../stores/setting';
import useUiStore from '../../stores/ui';
import useHighlightsStore from '../../stores/highlights';
import useReaderStore from '../../stores/reader';
import useMediaStore from '../../stores/media';
import useRenderer from '../use-renderer';
import useClickHandler from '../../composables/use-click-handler';
import useReaderEngine from '../use-reader-engine';
import useAudioTracks from '../../composables/use-audio-tracks';
import useAudioAnnotations from '../../composables/use-audio-annotations';
import { jqueryExtend } from '../../util/jqueryExtend';
import { getEpubContainerElement } from '../../util/book';
import {
  fixPageColors,
  fixPageStyles,
  getIsBookAtStartOrLocationIsNotKnownYet,
  repositionPages,
} from '../../util/documentFix';
import { storeToRefs } from 'pinia';
import { useDisplay, useTheme } from 'vuetify';
import dyslexic from '../../assets/open-dyslexic/style.css?inline';
import audioHighlights from '../../assets/audio-highlights.css?inline';

interface PaginatedReaderCallbacks {
  registerViewerElementTouchHandlers: () => void;
  registerBookPageTouchHandlers: (view: View) => void;
  onRelocated: (location: EpubLocation) => Promise<void>;
  onDisplayed: () => void;
}

// Fallback colors if vuetify theme is not available
export const DEFAULT_COLORS = {
  onBackground: '#000000',
  background: '#ffffff',
} as const;

/**
 * Composable for managing paginated (non-continuous) EPUB rendering
 * Handles page-based navigation, animations, and event management
 */
const usePaginatedReader = () => {
  const renditionStore = useRenditionStore();
  const { initializeRendition, setRendition } = renditionStore;
  const { serviceTheme } = storeToRefs(useReaderStore());
  const { isPageTurnAnimationEnabled } = storeToRefs(useSettingStore());

  const uiStore = useUiStore();
  const { animationDuration } = storeToRefs(uiStore);

  const clickHandler = useClickHandler();
  const { addStylesheetCss } = useRenderer();
  const vuetifyTheme = useTheme();
  const { platform } = useDisplay();

  /**
   * Handles content changes in iframe pages
   * Sets up stylesheets, keyboard navigation, and click handlers
   */
  const createContentChangeHandler = (isFrogBook: boolean, stylesheets: AdditionalStylesheets[]) => {
    return async (contents: Contents) => {
      const { viewMode } = storeToRefs(useReaderStore());
      const { applyThemeAndBackground } = useRenderer();
      // Add visual annotations for audio highlighting
      const { getPageSpineItem } = useReaderEngine();
      const { loadTracks } = useAudioTracks();
      const { isTextClickPlaybackEnabled } = storeToRefs(useSettingStore());
      const { isPlayingMainLanguage, currentPageTracks } = storeToRefs(useMediaStore());
      const { setCurrentTrack, setIsUserChangingTrack } = useMediaStore();
      const { addTrackClickHandlers } = useAudioAnnotations();
      const { reapplyAnnotationRects } = useHighlightsStore();

      // Apply unified theme and background
      applyThemeAndBackground(contents, viewMode.value);

      // We have added this method to add policy to assets urls in french epub books as they are using some custom jquery
      // to load the assets.
      // But as we now have bunny cdn that sign entire path.
      // Im unsure what this is doing. Frog books dont wanna play without it.
      // TODO: investigate whats inside jqueryExtend.
      if (isFrogBook) {
        jqueryExtend();
      }

      // Add custom stylesheets
      addStylesheetCss(contents, stylesheets);

      contents.addStylesheetCss(audioHighlights, 'ilt-audio-highlights');

      // Setup click handlers
      clickHandler.registerContents(contents);

      // Re-apply pixel-perfect underline rects for any user underlines on this page
      reapplyAnnotationRects(contents.document);

      const spineItem = getPageSpineItem(contents.sectionIndex);

      // Guard: only load tracks for currently-displayed pages, not EPUB.js pre-rendered next-spread iframes.
      // currentPageFiles is empty on initial load (before first relocated event), so we allow through then.
      const { currentPageFiles } = storeToRefs(useRenditionStore());
      const isCurrentlyDisplayed =
        currentPageFiles.value.length === 0 || currentPageFiles.value.some((f) => f.index === contents.sectionIndex);

      if (isCurrentlyDisplayed) {
        await loadTracks(spineItem);
      }

      if (isPlayingMainLanguage.value && isTextClickPlaybackEnabled.value) {
        const spineIndex = contents.sectionIndex;

        if (!contents.document || !contents.document.body) {
          return;
        }

        // Filter to tracks that belong to THIS specific iframe by matching spinePos
        const tracksForThisSpine = currentPageTracks.value.filter((track) => {
          if (!track.cfi) return false;
          const parsedCfi = new EpubCFI(track.cfi);
          return parsedCfi.spinePos === spineIndex;
        });

        // Only add handlers if this spine actually has tracks
        if (tracksForThisSpine.length > 0) {
          addTrackClickHandlers(
            tracksForThisSpine,
            (clickedTrack) => {
              setCurrentTrack(clickedTrack);
              setIsUserChangingTrack(true);
            },
            spineIndex,
            contents.document,
          );
        }
      }
    };
  };

  const addClickListenerToIframe = (contents: Contents) => {
    const { addEventToIframeContents } = useRenderer();
    addEventToIframeContents(contents, 'click', (event: MouseEvent) => {
      window.parent.postMessage({ type: 'iframe-click' }, '*');
    });
  };

  const addKeyboardNavigationToIframe = (contents: Contents) => {
    const { addEventToIframeContents } = useRenderer();
    addEventToIframeContents(contents, 'keydown', (event: KeyboardEvent) => {
      // eslint-disable-next-line default-case
      switch (event.key) {
        case 'ArrowRight':
          window.parent.postMessage({ type: 'keyboard-navigation', direction: 'next' }, '*');
          break;
        case 'ArrowLeft':
          window.parent.postMessage({ type: 'keyboard-navigation', direction: 'previous' }, '*');
          break;
      }

      if (event.altKey && event.key === 'ArrowRight') {
        event.preventDefault();
        window.parent.postMessage({ type: 'keyboard-navigation', direction: 'next' }, '*');
      } else if (event.altKey && event.key === 'ArrowLeft') {
        event.preventDefault();
        window.parent.postMessage({ type: 'keyboard-navigation', direction: 'previous' }, '*');
      }

      if (event.code === 'Space' && !event.metaKey && !event.ctrlKey && !event.altKey && !event.shiftKey) {
        event.preventDefault();
        window.parent.postMessage({ type: 'keyboard-shortcut', action: 'toggle-player' }, '*');
      } else if (event.shiftKey && (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'p') {
        event.preventDefault();
        window.parent.postMessage({ type: 'keyboard-shortcut', action: 'toggle-player' }, '*');
      } else if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'i') {
        event.preventDefault();
        window.parent.postMessage({ type: 'keyboard-shortcut', action: 'toggle-toc' }, '*');
      } else if (event.key.toLowerCase() === 'm' && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        window.parent.postMessage({ type: 'keyboard-shortcut', action: 'toggle-mute' }, '*');
      } else if (event.key === 'ArrowUp' && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        window.parent.postMessage({ type: 'keyboard-shortcut', action: 'volume-up' }, '*');
      } else if (event.key === 'ArrowDown' && !event.metaKey && !event.ctrlKey && !event.altKey) {
        event.preventDefault();
        window.parent.postMessage({ type: 'keyboard-shortcut', action: 'volume-down' }, '*');
      } else if (event.key === 'Escape') {
        event.preventDefault();
        window.parent.postMessage({ type: 'keyboard-shortcut', action: 'close-overlays' }, '*');
      }
    });
  };

  /**
   * Applies dyslexic font CSS to all rendered pages
   */
  const applyDyslexicFont = (rendition: Rendition) => {
    const contents = rendition.getContents() as unknown as Contents[];
    contents.forEach((c) => {
      c.addStylesheetCss(dyslexic, 'dyslexicFontStyle');
    });
  };

  /**
   * Gets theme colors with fallbacks
   */
  const getThemeColors = (value: string) => {
    const theme = vuetifyTheme.themes.value[value] ?? vuetifyTheme.current.value;

    return {
      pageBackground: theme?.colors['surface-container-lowest'] ?? DEFAULT_COLORS.background,
      viewerBackground: theme?.colors.surface ?? DEFAULT_COLORS.background,
      text: theme?.colors['on-surface'] ?? DEFAULT_COLORS.onBackground,
    };
  };

  const applyPageColors = (view: View) => {
    const { isReflowable } = storeToRefs(useRenditionStore());
    const { viewMode, isFrogBook } = storeToRefs(useReaderStore());
    const colors = getThemeColors(serviceTheme.value);

    fixPageColors(
      view,
      viewMode.value,
      isReflowable.value,
      isFrogBook.value,
      colors.text,
      colors.pageBackground,
      colors.viewerBackground,
    );
  };

  /**
   * Sets up page view handlers when new pages are added
   * Applies styling and positioning to newly created pages
   */
  const createPageAddedHandler = (rendition: Rendition, registerBookPageTouchHandlers: (view: View) => void) => {
    return (view: View) => {
      // Access stores directly
      const { isReflowable, hasSpreadNone } = storeToRefs(useRenditionStore());
      const { viewMode, isFrogBook } = storeToRefs(useReaderStore());
      const { isPageDisplayModeSingle } = storeToRefs(useSettingStore());
      const { manifest } = useReaderEngine();

      // Derive isRTL from parsed manifest
      const isRTL = manifest.value.direction === 'rtl';

      // @ts-ignore Rendition does have a manager, although we know is not recommended to access it
      const allViews: View[] = rendition.manager.views.all();
      const isTouch = platform.value?.touch;

      // Apply page styles
      fixPageStyles(view, viewMode.value, isReflowable.value);

      // Apply page colors
      applyPageColors(view);

      // Determine if we're in single-page mode (user preference or hasSpreadNone)
      const isSinglePageMode = hasSpreadNone.value || isPageDisplayModeSingle.value;

      // Position pages
      repositionPages(
        allViews,
        viewMode.value,
        isPageTurnAnimationEnabled.value,
        isRTL,
        isReflowable.value,
        isFrogBook.value,
        /*
         We consider undefined as the start of the book, although is not completely accurate, because if we start the book
         at a different location, the location will be undefined until the location is reported.
         ⚠️ We rely on the fact that the page index will also be checked when positioning pages.
         */
        getIsBookAtStartOrLocationIsNotKnownYet(rendition),
        isTouch,
        isSinglePageMode,
      );

      // Pre-center preloaded views to prevent visible jump during slide animation (SlidingIframeView only)
      // @ts-ignore - accessing manager.views
      const currentView = rendition.manager.views._views.find((v: View) => v.displayed && v !== view);
      if (currentView && 'preCenterContent' in view && typeof (view as any).preCenterContent === 'function') {
        (view as any).preCenterContent(currentView);
      }

      // Register touch handlers for the page
      registerBookPageTouchHandlers(view);
    };
  };

  /**
   * Sets up animation duration on the epub container
   */
  const setupAnimationDuration = () => {
    const epubContainer = getEpubContainerElement();
    if (epubContainer) {
      // TODO: move this animation and starting using computed instead.
      // TODO - double check if the animation is still used. There might be no need for it.
      epubContainer.style.animationDuration = `${animationDuration.value}s`;
    }
  };

  /**
   * Creates the relocated event handler
   * Handles page repositioning and state updates after navigation
   */
  const createRelocatedHandler = (rendition: Rendition, onRelocated: (location: EpubLocation) => Promise<void>) => {
    return async (location: EpubLocation) => {
      // Apply dyslexic font if needed
      applyDyslexicFont(rendition);

      // Delegate main relocation logic to Reader.vue
      await onRelocated(location);

      // Access stores directly
      const { isReflowable, hasSpreadNone } = storeToRefs(useRenditionStore());
      const { viewMode, isFrogBook } = storeToRefs(useReaderStore());
      const { isPageDisplayModeSingle } = storeToRefs(useSettingStore());
      const { manifest } = useReaderEngine();

      // Derive isRTL from parsed manifest
      const isRTL = manifest.value.direction === 'rtl';

      // Determine if we're in single-page mode (user preference or hasSpreadNone)
      const isSinglePageMode = hasSpreadNone.value || isPageDisplayModeSingle.value;

      // Reposition pages after navigation
      // @ts-ignore - manager exists but not in types
      const allViews: View[] = rendition.manager.views.all();
      repositionPages(
        allViews,
        viewMode.value,
        isPageTurnAnimationEnabled.value,
        isRTL,
        isReflowable.value,
        isFrogBook.value,
        getIsBookAtStartOrLocationIsNotKnownYet(rendition),
        platform.value.touch,
        isSinglePageMode,
      );

      // Notify Reader.vue that relocation is complete
      window.parent.postMessage({ type: 'relocated-complete' }, '*');
    };
  };

  /**
   * Sets up the rendition started event
   * Initializes rendition and registers all event handlers
   */
  const setupStartedEvent = (rendition: Rendition, callbacks: PaginatedReaderCallbacks) => {
    rendition.on('started', () => {
      // Initialize rendition store
      initializeRendition(rendition);

      /*
        We reposition the pages on EVENTS.MANAGERS.ADDED because it's the earliest event we can listen to after a new page
        is created, thus reducing (eliminating) the flickering effect when the page is moved around.
        */
      // @ts-ignore Rendition does have a manager, although we know is not recommended to access it
      rendition.manager.on('added', createPageAddedHandler(rendition, callbacks.registerBookPageTouchHandlers));

      // SliderManager emits 'added' only once a view becomes current, so preloaded pages would otherwise slide in
      // without the theme colors and flash white in dark mode.
      // @ts-ignore Rendition does have a manager, although we know is not recommended to access it
      rendition.manager.on('preloaded', applyPageColors);

      // Setup animation duration
      setupAnimationDuration();

      // Setup relocated handler
      rendition.on('relocated', createRelocatedHandler(rendition, callbacks.onRelocated));

      // Setup audio track click handlers
      setupAudioTrackClickHandlers(rendition);

      // Store rendition reference
      setRendition(rendition);
    });
  };

  /**
   * Sets up the rendition displayed event
   * Triggers after the book is initially rendered
   */
  const setupDisplayedEvent = (rendition: Rendition, onDisplayed: () => void) => {
    rendition.on('displayed', () => {
      onDisplayed();
      window.parent.postMessage({ type: 'displayed-complete' }, '*');
      const { reapplyAnnotationRects } = useHighlightsStore();
      (rendition.getContents() as unknown as Contents[]).forEach((c) => reapplyAnnotationRects(c.document));
    });
  };

  /**
   * Sets up audio track click handlers using epub.js annotations API
   * Enables click-to-play functionality for audio tracks
   */
  const setupAudioTrackClickHandlers = (rendition: Rendition) => {
    const mediaStore = useMediaStore();
    const { isTextClickPlaybackEnabled } = storeToRefs(useSettingStore());

    rendition.on('markClicked', (cfiRange: string, data: any) => {
      if (data?.trackId && isTextClickPlaybackEnabled.value) {
        const { currentPageTracks } = storeToRefs(mediaStore);
        const track = currentPageTracks.value.find((t) => t.text.srcFragmentId === data.trackId);

        if (track) {
          mediaStore.setCurrentTrack(track);
          mediaStore.playing = true;
          mediaStore.setIsUserChangingTrack(true);
        }
      }
    });
  };

  /**
   * The main setup function for the paginated reader
   * Configures all event handlers and initializes the rendering system
   */
  const setup = (rendition: Rendition, stylesheets: AdditionalStylesheets[], callbacks: PaginatedReaderCallbacks) => {
    // Access stores directly
    const { isFrogBook } = storeToRefs(useReaderStore());

    // Register viewer-level touch handlers
    callbacks.registerViewerElementTouchHandlers();

    // Register content change handler
    const contentChangeHandler = createContentChangeHandler(isFrogBook.value, stylesheets);
    rendition.hooks.content.register(contentChangeHandler);

    rendition.hooks.content.register((contents: Contents) => addClickListenerToIframe(contents));
    rendition.hooks.content.register((contents: Contents) => addKeyboardNavigationToIframe(contents));
    // Setup rendition lifecycle events
    setupStartedEvent(rendition, callbacks);
    setupDisplayedEvent(rendition, callbacks.onDisplayed);
  };

  return {
    setup,
    getThemeColors,
  };
};

export default usePaginatedReader;
